/**
 * The one entry card to the Changes pages, shown on Docs › Requirements and
 * on Insights (Learn). Same card, two sentences: the Requirements copy says
 * what a change page holds; the Insights copy says what the Learn stage does.
 * The counts are the workspace's own (GET /changes); while they load, or when
 * they cannot be read, the card is the sentence and the link.
 */
import { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ArrowRight, Waypoints } from 'lucide-react';
import { captureError } from '@/lib/sentry';
import { stageOf, waitingForYourReview, type ChangeSummary } from '@/lib/changes/model';
import { changesApi } from '@/services/changesApi';

const COPY = {
  requirements: {
    heading: 'Changes',
    sentence:
      'A change is one page: the decision, the contract, the behaviour and the plan — approved in one sitting, built, released, learnt from.',
  },
  learn: {
    heading: 'Changes in Learn',
    sentence:
      'Stage four, Learn: every merge is recorded, every decision is registered, and a defect becomes a rule, a check or a skill line in the same week.',
  },
} as const;

export function ChangesEntryCard({ variant, className }: { variant: keyof typeof COPY; className?: string }) {
  // Keep the query string on the in-SPA link — on localhost ?tenant IS the host selection.
  const { search } = useLocation();
  const copy = COPY[variant];
  const [changes, setChanges] = useState<ChangeSummary[] | null>(null);

  useEffect(() => {
    let live = true;
    changesApi.listAllChanges({ archived: true }).then(
      (items) => { if (live) setChanges(items); },
      (error) => captureError(error, { component: 'ChangesEntryCard' }),
    );
    return () => { live = false; };
  }, []);

  const inLearn = changes?.filter((c) => stageOf(c) === 'learn').length ?? 0;
  const inFlight = (changes?.length ?? 0) - inLearn;

  return (
    <div className={`rounded-xl border border-violet-200 bg-violet-50/40 p-4 dark:border-violet-900/60 dark:bg-violet-950/20 ${className ?? ''}`}>
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-violet-100 dark:bg-violet-900/40">
          <Waypoints className="h-5 w-5 text-violet-600 dark:text-violet-300" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">{copy.heading}</h3>
            {changes && (
              <span data-change-counts className="text-xs text-slate-500 dark:text-slate-400">
                {variant === 'learn' ? `${inLearn} in Learn` : `${inFlight} in flight`} · {waitingForYourReview(changes).length} waiting for your review
              </span>
            )}
          </div>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-400">{copy.sentence}</p>
          <Link
            to={{ pathname: '/changes', search }}
            className="mt-2 inline-flex items-center gap-1 text-sm font-medium text-violet-700 hover:underline dark:text-violet-300"
          >
            Open changes
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </div>
  );
}
